import floridaMap from "@highcharts/map-collection/countries/us/us-fl-all.geo.json";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import Highcharts from "highcharts";
import Funnel from "highcharts/modules/funnel";
import MapModule from "highcharts/modules/map";
import HighchartsReact from "highcharts-react-official";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { type ColumnData } from "~types/datagrid";
import { DataGrid } from "~ui/molecules/DataGrid/DataGrid";

Funnel(Highcharts);
MapModule(Highcharts);

type ChartView = "enrollment" | "location" | "outcomes" | "pipeline";

const CHART_VIEW_STORAGE_KEY = "apprenticeshipDashboard.chartView";

const chartViewOptions: { label: string; value: ChartView }[] = [
  { label: "Pipeline Funnel", value: "pipeline" },
  { label: "Enrollment Over Time", value: "enrollment" },
  { label: "Outcomes", value: "outcomes" },
  { label: "Florida Location", value: "location" },
];

const getInitialChartView = (): ChartView => {
  const stored = localStorage.getItem(CHART_VIEW_STORAGE_KEY);
  if (stored === "pipeline" || stored === "enrollment" || stored === "outcomes" || stored === "location") {
    return stored;
  }
  return "pipeline";
};

/** Mock apprenticeship counts by Florida county (hc-key from map-collection) */
const MOCK_LOCATION_DATA: { count: number; county: string; hcKey: string }[] = [
  { count: 85, county: "Miami-Dade", hcKey: "us-fl-086" },
  { count: 72, county: "Broward", hcKey: "us-fl-011" },
  { count: 58, county: "Palm Beach", hcKey: "us-fl-099" },
  { count: 45, county: "Hillsborough", hcKey: "us-fl-057" },
  { count: 52, county: "Orange", hcKey: "us-fl-095" },
  { count: 38, county: "Pinellas", hcKey: "us-fl-103" },
  { count: 41, county: "Duval", hcKey: "us-fl-031" },
  { count: 34, county: "Lee", hcKey: "us-fl-071" },
  { count: 28, county: "Polk", hcKey: "us-fl-105" },
  { count: 31, county: "Brevard", hcKey: "us-fl-009" },
  { count: 22, county: "Volusia", hcKey: "us-fl-127" },
  { count: 26, county: "Pasco", hcKey: "us-fl-101" },
  { count: 29, county: "Seminole", hcKey: "us-fl-117" },
  { count: 19, county: "Sarasota", hcKey: "us-fl-115" },
  { count: 15, county: "Manatee", hcKey: "us-fl-081" },
  { count: 18, county: "Collier", hcKey: "us-fl-021" },
  { count: 14, county: "St. Lucie", hcKey: "us-fl-111" },
  { count: 12, county: "Leon", hcKey: "us-fl-073" },
  { count: 11, county: "Alachua", hcKey: "us-fl-001" },
  { count: 8, county: "Clay", hcKey: "us-fl-019" },
  { count: 5, county: "Nassau", hcKey: "us-fl-089" },
  { count: 9, county: "Escambia", hcKey: "us-fl-033" },
  { count: 10, county: "St. Johns", hcKey: "us-fl-109" },
  { count: 7, county: "Lake", hcKey: "us-fl-069" },
  { count: 6, county: "Marion", hcKey: "us-fl-083" },
];

/** Mock data matching GET /api/insights/apprenticeship-pipeline */
const MOCK_PIPELINE_DATA = [
  { count: 1240, stage: "Interested" },
  { count: 870, stage: "Eligible" },
  { count: 520, stage: "Interviewed" },
  { count: 310, stage: "Accepted" },
  { count: 260, stage: "Active Apprentices" },
  { count: 190, stage: "Completed" },
];

/** Mock data matching GET /api/insights/apprenticeship-enrollment-over-time */
const MOCK_ENROLLMENT_DATA = [
  { activeApprentices: 120, date: "2025-01", newEnrollments: 42 },
  { activeApprentices: 146, date: "2025-02", newEnrollments: 55 },
  { activeApprentices: 170, date: "2025-03", newEnrollments: 61 },
  { activeApprentices: 198, date: "2025-04", newEnrollments: 58 },
  { activeApprentices: 225, date: "2025-05", newEnrollments: 52 },
  { activeApprentices: 260, date: "2025-06", newEnrollments: 48 },
];

/** Mock data matching GET /api/insights/apprenticeship-outcomes */
const MOCK_OUTCOMES_DATA = [
  { category: "Completed", count: 190 },
  { category: "Active", count: 260 },
  { category: "Withdrawn", count: 45 },
  { category: "Placed in Job", count: 150 },
];

interface PipelineRow {
  rowId: string;
  stage: string;
  count: number;
}

interface EnrollmentRow {
  rowId: string;
  date: string;
  newEnrollments: number;
  activeApprentices: number;
}

interface OutcomesRow {
  rowId: string;
  category: string;
  count: number;
}

interface LocationRow {
  rowId: string;
  county: string;
  count: number;
}

const defaultTitle = "Apprenticeship Metrics";
const defaultSubtitle = "Track apprenticeship pipeline, enrollment, and outcomes across the platform";

export interface ApprenticeshipDashboardProps {
  subtitle?: string;
  title?: string;
}

export const ApprenticeshipDashboard = ({
  title = defaultTitle,
  subtitle = defaultSubtitle,
}: ApprenticeshipDashboardProps) => {
  const [chartView, setChartView] = useState<ChartView>(getInitialChartView);

  const handleChartViewChange = (event: SelectChangeEvent) => {
    const newView = event.target.value as ChartView;
    setChartView(newView);
    localStorage.setItem(CHART_VIEW_STORAGE_KEY, newView);
  };

  const chartOptions: Highcharts.Options = useMemo(() => {
    const commonChart = { animation: false };

    if (chartView === "location") {
      return {
        chart: {
          ...commonChart,
          map: floridaMap as Highcharts.MapDataType,
        },
        colorAxis: {
          maxColor: "#1E88E5",
          min: 0,
          minColor: "#E8F4F8",
        },
        legend: { align: "right", layout: "vertical", verticalAlign: "middle" },
        series: [
          {
            data: MOCK_LOCATION_DATA.map((d) => ({ key: d.hcKey, value: d.count })),
            joinBy: ["hc-key", "key"],
            name: "Apprentices",
            type: "map",
          } as Highcharts.SeriesMapOptions,
        ],
        title: { text: "Apprenticeships by County (Florida)" },
        tooltip: {
          pointFormat: "<b>{point.name}</b><br/>Apprentices: {point.value}",
        },
      };
    }

    if (chartView === "pipeline") {
      return {
        chart: { ...commonChart, type: "funnel" },
        plotOptions: {
          funnel: {
            dataLabels: {
              enabled: true,
              format: "<b>{point.name}</b>: {point.y}",
              softConnector: true,
            },
            neckHeight: "25%",
            neckWidth: "30%",
          },
        },
        series: [
          {
            data: MOCK_PIPELINE_DATA.map((d) => ({ name: d.stage, y: d.count })),
            name: "Candidates",
            type: "funnel",
          } as Highcharts.SeriesOptionsType,
        ],
        title: { text: "Apprenticeship Pipeline Funnel" },
        tooltip: { pointFormat: "<b>{point.y}</b> candidates" },
      };
    }

    if (chartView === "enrollment") {
      const enrollmentDates = MOCK_ENROLLMENT_DATA.map((d) =>
        DateTime.fromFormat(d.date, "yyyy-MM").toFormat("MMM yyyy"),
      );
      return {
        chart: { ...commonChart, type: "line" },
        legend: { enabled: true },
        plotOptions: {
          line: { marker: { enabled: true } },
        },
        series: [
          {
            data: MOCK_ENROLLMENT_DATA.map((d) => d.newEnrollments),
            name: "New Enrollments",
            type: "line",
          },
          {
            data: MOCK_ENROLLMENT_DATA.map((d) => d.activeApprentices),
            name: "Active Apprentices",
            type: "line",
          },
        ] as Highcharts.SeriesOptionsType[],
        title: { text: "Apprenticeship Enrollment Over Time" },
        tooltip: { shared: true },
        xAxis: { categories: enrollmentDates, title: { text: "Month" } },
        yAxis: { min: 0, title: { text: "Count" } },
      };
    }

    return {
      chart: { ...commonChart, type: "column" },
      legend: { enabled: true },
      plotOptions: {
        column: {
          dataLabels: { enabled: true, format: "{point.y}" },
          stacking: "normal",
        },
      },
      series: MOCK_OUTCOMES_DATA.map((d) => ({
        data: [d.count],
        name: d.category,
        type: "column",
      })) as Highcharts.SeriesOptionsType[],
      title: { text: "Apprenticeship Outcomes" },
      tooltip: { pointFormat: "<b>{point.y}</b> apprentices" },
      xAxis: { categories: ["Outcomes"], title: { text: "Category" } },
      yAxis: {
        min: 0,
        stackLabels: { enabled: true },
        title: { text: "Number of Apprentices" },
      },
    };
  }, [chartView]);

  const pipelineRows: PipelineRow[] = useMemo(
    () =>
      MOCK_PIPELINE_DATA.map((d) => ({
        count: d.count,
        rowId: d.stage,
        stage: d.stage,
      })),
    [],
  );

  const pipelineColumns = useMemo<ColumnData<PipelineRow>[]>(
    () => [
      { field: "stage", flex: 2, headerName: "Stage", minWidth: 150 },
      { align: "right", field: "count", flex: 1, headerAlign: "right", headerName: "Count", minWidth: 100 },
    ],
    [],
  );

  const enrollmentRows: EnrollmentRow[] = useMemo(
    () =>
      MOCK_ENROLLMENT_DATA.map((d) => ({
        activeApprentices: d.activeApprentices,
        date: DateTime.fromFormat(d.date, "yyyy-MM").toFormat("MMM yyyy"),
        newEnrollments: d.newEnrollments,
        rowId: d.date,
      })),
    [],
  );

  const enrollmentColumns = useMemo<ColumnData<EnrollmentRow>[]>(
    () => [
      { field: "date", flex: 2, headerName: "Month", minWidth: 120 },
      {
        align: "right",
        field: "newEnrollments",
        flex: 1,
        headerAlign: "right",
        headerName: "New Enrollments",
        minWidth: 120,
      },
      {
        align: "right",
        field: "activeApprentices",
        flex: 1,
        headerAlign: "right",
        headerName: "Active Apprentices",
        minWidth: 140,
      },
    ],
    [],
  );

  const outcomesRows: OutcomesRow[] = useMemo(
    () =>
      MOCK_OUTCOMES_DATA.map((d) => ({
        category: d.category,
        count: d.count,
        rowId: d.category,
      })),
    [],
  );

  const outcomesColumns = useMemo<ColumnData<OutcomesRow>[]>(
    () => [
      { field: "category", flex: 2, headerName: "Outcome", minWidth: 150 },
      { align: "right", field: "count", flex: 1, headerAlign: "right", headerName: "Count", minWidth: 100 },
    ],
    [],
  );

  const locationRows: LocationRow[] = useMemo(
    () =>
      [...MOCK_LOCATION_DATA]
        .sort((a, b) => b.count - a.count)
        .map((d) => ({
          count: d.count,
          county: d.county,
          rowId: d.hcKey,
        })),
    [],
  );

  const locationColumns = useMemo<ColumnData<LocationRow>[]>(
    () => [
      { field: "county", flex: 2, headerName: "County", minWidth: 150 },
      { align: "right", field: "count", flex: 1, headerAlign: "right", headerName: "Apprentices", minWidth: 100 },
    ],
    [],
  );

  const renderTable = () => {
    if (chartView === "location") {
      return (
        <DataGrid<LocationRow>
          columns={locationColumns}
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          paginationMode="client"
          rowHeight={40}
          rows={locationRows}
          slotProps={{
            container: { sx: { height: "auto", maxHeight: 400, minHeight: "unset" } },
            empty: { message: "No data available" },
          }}
          slots={{ toolbar: () => null }}
          sortingMode="client"
        />
      );
    }
    if (chartView === "pipeline") {
      return (
        <DataGrid<PipelineRow>
          columns={pipelineColumns}
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          paginationMode="client"
          rowHeight={40}
          rows={pipelineRows}
          slotProps={{
            container: { sx: { height: "auto", maxHeight: 400, minHeight: "unset" } },
            empty: { message: "No data available" },
          }}
          slots={{ toolbar: () => null }}
          sortingMode="client"
        />
      );
    }
    if (chartView === "enrollment") {
      return (
        <DataGrid<EnrollmentRow>
          columns={enrollmentColumns}
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          paginationMode="client"
          rowHeight={40}
          rows={enrollmentRows}
          slotProps={{
            container: { sx: { height: "auto", maxHeight: 400, minHeight: "unset" } },
            empty: { message: "No data available" },
          }}
          slots={{ toolbar: () => null }}
          sortingMode="client"
        />
      );
    }
    return (
      <DataGrid<OutcomesRow>
        columns={outcomesColumns}
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="client"
        rowHeight={40}
        rows={outcomesRows}
        slotProps={{
          container: { sx: { height: "auto", maxHeight: 400, minHeight: "unset" } },
          empty: { message: "No data available" },
        }}
        slots={{ toolbar: () => null }}
        sortingMode="client"
      />
    );
  };

  return (
    <Box sx={{ mb: 2, pt: 1 }}>
      <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, mb: 3, p: 2 }}>
        <Typography sx={{ mb: 1 }} variant="h6">
          How to Read This Data
        </Typography>
        <Typography color="text.secondary" variant="body2">
          <strong>Pipeline Funnel:</strong> Shows how candidates move from initial interest through completed
          apprenticeship. Use this to identify bottlenecks in recruitment, eligibility, interviews, and completions.
          <br />
          <br />
          <strong>Enrollment Over Time:</strong> Tracks new enrollments and total active apprentices by month.
          <br />
          <br />
          <strong>Outcomes:</strong> Displays apprenticeship outcomes including completions, withdrawals, and placements
          into jobs.
          <br />
          <br />
          <strong>Florida Location:</strong> Heatmap showing apprenticeship density across Florida counties. Darker
          shades indicate higher concentrations.
          <br />
          <br />
          <em>This dashboard uses mocked data. Connect to the API endpoints to display live data.</em>
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={12}>
          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ minWidth: 200 }}>
              <Typography sx={{ mb: 0.5 }} variant="h6">
                {title}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {subtitle}
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="apprenticeship-chart-view-label">View</InputLabel>
              <Select
                id="apprenticeship-chart-view-select"
                label="View"
                labelId="apprenticeship-chart-view-label"
                onChange={handleChartViewChange}
                value={chartView}
              >
                {chartViewOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid container size={12} spacing={2}>
          <Grid size={{ xl: 6, xs: 12 }}>
            <Card elevation={1}>
              <CardContent>
                <Box sx={{ minHeight: 400 }}>
                  <HighchartsReact
                    constructorType={chartView === "location" ? "mapChart" : "chart"}
                    highcharts={Highcharts}
                    key={chartView}
                    options={chartOptions}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xl: 6, xs: 12 }}>
            <Box sx={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}>{renderTable()}</Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
